import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PropRow } from '../activity-drawer/ui/PropRow';

interface InlineTextRowProps {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
  /**
   * Salvataggio immediato. Ritorna false (o lancia) se fallito: la riga
   * ripristina il valore precedente — mai mostrare come salvato un valore
   * che nel DB non c'è. Il toast di esito è a carico del chiamante.
   */
  onSave: (value: string | null) => Promise<boolean>;
  canEdit: boolean;
  type?: string;
  placeholder?: string;
  /** Validazione pre-save: ritorna il messaggio d'errore o null se ok. */
  validate?: (value: string) => string | null;
  /** Formattazione del valore a riposo (es. €). */
  display?: (value: string) => React.ReactNode;
  isLink?: boolean;
  /** Azioni ghost a destra (copia, chiama...): hover-only, sempre visibili su touch. */
  actions?: React.ReactNode;
  /** Click sulla riga quando NON si può editare (es. copia). */
  onReadOnlyClick?: () => void;
}

/**
 * Riga di proprietà con edit inline a salvataggio immediato: click sul valore
 * apre l'input, Invio/blur salva, Escape annulla e ripristina.
 */
export function InlineTextRow({
  icon, label, value, onSave, canEdit, type = 'text', placeholder = 'Vuoto',
  validate, display, isLink, actions, onReadOnlyClick,
}: InlineTextRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Evita il doppio salvataggio quando Invio (che salva) fa scattare anche il blur.
  const settledRef = useRef(false);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEdit = () => {
    if (!canEdit || saving) {
      onReadOnlyClick?.();
      return;
    }
    setDraft(value || '');
    setError(null);
    settledRef.current = false;
    setEditing(true);
  };

  const cancel = () => {
    settledRef.current = true;
    setEditing(false);
    setError(null);
  };

  const commit = async () => {
    if (settledRef.current) return;
    settledRef.current = true;
    const next = draft.trim() || null;
    if (next === (value || null)) {
      setEditing(false);
      return;
    }
    if (next !== null && validate) {
      const msg = validate(next);
      if (msg) {
        setError(msg);
        setEditing(false);
        return;
      }
    }
    setEditing(false);
    setSaving(true);
    try {
      const ok = await onSave(next);
      if (!ok) setError(null); // il valore mostrato resta quello vecchio (prop invariata)
    } catch {
      // idem: la prop `value` non è cambiata, la riga torna com'era
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div>
        <PropRow icon={icon} label={label}>
          <input data-escape-local
            ref={inputRef}
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') { e.preventDefault(); cancel(); }
            }}
            placeholder={placeholder}
            aria-label={label}
            className="w-full min-w-0 flex-1 bg-transparent text-[13px] text-[#37352F] dark:text-white placeholder-[rgba(55,53,47,0.4)] dark:placeholder-gray-500 focus:outline-none"
          />
        </PropRow>
      </div>
    );
  }

  return (
    <div>
      <PropRow
        icon={icon}
        label={label}
        onClick={startEdit}
        trailing={
          saving ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} /> : actions
        }
      >
        {value ? (
          <span className={`truncate ${isLink ? 'text-[#337EA9] dark:text-[#529CCA]' : ''}`}>
            {display ? display(value) : value}
          </span>
        ) : (
          <span className="text-[rgba(55,53,47,0.45)] dark:text-gray-500">Vuoto</span>
        )}
      </PropRow>
      {error && <p className="ml-[8.5rem] text-[11px] text-red-500">{error}</p>}
    </div>
  );
}
